import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import apiClient from '../../services/apiClient';
import HelpPanel, { HelpToggleBtn } from '../../components/common/HelpPanel';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';
import { ZONA_ARGENTINA } from '../../utils/dateUtils';

const TIPOS_GPS = [
  { value: 'aemp',        label: 'AEMP 2.0 / ISO 15143-3 (CAT, Komatsu, JD…)' },
  { value: 'powerfleet',  label: 'Powerfleet / MiX Telematics' },
  { value: 'geotab',      label: 'Geotab MyGeotab' },
  { value: 'sitrack',     label: 'Sitrack (manual/push)' },
  { value: 'wara',        label: 'Wara GPS (manual/push)' },
  { value: 'generic_rest', label: 'REST genérico (personalizado)' },
];

const Page = styled.div`
  padding: 0.85rem 1rem 1rem;
  background: ${({ theme }) => theme.colors.bgPrincipal};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  display: flex; flex-direction: column;
  min-height: calc((100vh / var(--app-zoom, 1)) - 80px);
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 0.75rem; padding-bottom: 0.45rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.info};
`;
const Title = styled.h1`
  color: ${({ theme }) => theme.colors.info}; font-size: 1.25rem;
  margin: 0; font-weight: 500; display: flex; align-items: center; gap: 0.45rem;
`;
const Controls = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem;
`;
const SearchInput = styled.input`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.4rem 0.65rem; border-radius: 4px; font-size: 0.8rem; width: 240px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
  &::placeholder { color: ${({ theme }) => theme.colors.txtSecundario}; }
`;
const AddBtn = styled.button`
  background: ${({ theme }) => theme.colors.info}; color: ${({ theme }) => theme.colors.onPrimary};
  border: none; padding: 0.45rem 0.9rem; border-radius: 4px;
  font-size: 0.8rem; font-weight: 500; cursor: pointer;
  display: flex; align-items: center; gap: 0.35rem;
  &:hover { background: ${({ theme }) => theme.colors.infoHover}; }
`;
const TableWrap = styled.div`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 8px; overflow: hidden; flex: 1; overflow-y: auto;
  max-height: calc((100vh / var(--app-zoom, 1)) - ${({ theme }) => theme.heights.navbar} - ${({ theme }) => theme.heights.cintaOpciones} - ${({ theme }) => theme.heights.footer} - 165px);
`;
const Table = styled.table`width: 100%; border-collapse: collapse;`;
const Th = styled.th`
  padding: 0.5rem 0.65rem; text-align: left; font-weight: 600;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border-bottom: 2px solid ${({ theme }) => theme.colors.borderDefault};
  font-size: 0.8rem; background: ${({ theme }) => theme.colors.bgSecundario};
  position: sticky; top: 0; z-index: 1;
`;
const Td = styled.td`
  padding: 0.55rem 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  font-size: 0.8rem;
`;
const ActionsCell = styled(Td)`
  text-align: right; white-space: nowrap;
  & > * { opacity: 0; pointer-events: none; transition: opacity 0.15s; }
  tr:hover & > *, tr:focus-within & > * { opacity: 1; pointer-events: auto; }
`;
const ActionBtn = styled.button`
  background: none;
  border: 1px solid ${({ $danger, theme }) => $danger ? theme.colors.danger : theme.colors.borderDefault};
  color: ${({ $danger, theme }) => $danger ? theme.colors.danger : theme.colors.txtSecundario};
  border-radius: 4px; padding: 3px 8px; font-size: 0.75rem; cursor: pointer; margin-left: 4px;
  &:hover { background: ${({ $danger, theme }) => $danger ? `${theme.colors.danger}20` : theme.colors.surfaceHover}; }
`;
const Badge = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 999px; font-size: 0.72rem; font-weight: 600;
  background: ${({ $on, theme }) => $on ? `${theme.colors.success}22` : `${theme.colors.danger}22`};
  color: ${({ $on, theme }) => $on ? theme.colors.success : theme.colors.danger};
  border: 1px solid ${({ $on, theme }) => $on ? `${theme.colors.success}55` : `${theme.colors.danger}55`};
`;
const Overlay = styled.div`
  position: fixed; inset: 0;
  background: ${({ theme }) => theme.colors.overlayBackdropStrong};
  display: flex; align-items: center; justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal}; padding: 1rem;
`;
const ModalBox = styled.div`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 8px; width: 100%; max-width: 560px;
  display: flex; flex-direction: column;
  max-height: calc((100vh / var(--app-zoom, 1)) - 2rem);
`;
const ModalHead = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.75rem 1rem; border-bottom: 1px solid ${({ theme }) => theme.colors.info};
`;
const ModalTitle = styled.h2`margin: 0; font-size: 1.1rem; font-weight: 500; color: ${({ theme }) => theme.colors.info};`;
const CloseBtn = styled.button`
  background: none; border: none; font-size: 1.4rem; cursor: pointer;
  color: ${({ theme }) => theme.colors.txtSecundario};
  &:hover { color: ${({ theme }) => theme.colors.danger}; }
`;
const ModalBody = styled.div`padding: 1rem; overflow-y: auto; flex: 1;`;
const ModalFoot = styled.div`
  display: flex; justify-content: flex-end; gap: 0.5rem;
  padding: 0.65rem 1rem; border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.bgSecundario};
`;
const FormGrp = styled.div`margin-bottom: 0.85rem;`;
const Lbl = styled.label`
  display: block; margin-bottom: 0.4rem; font-weight: 500; font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.txtPrincipal};
`;
const Inp = styled.input`
  width: 100%; background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.5rem 0.75rem; border-radius: 6px; font-size: 0.95rem;
  min-height: 34px; box-sizing: border-box;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.info}33`}; }
`;
const StyledSelect = styled.select`
  width: 100%; background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.5rem 0.75rem; border-radius: 6px; font-size: 0.95rem; min-height: 34px; cursor: pointer;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
  option { background: ${({ theme }) => theme.colors.surface}; }
`;
const Textarea = styled.textarea`
  width: 100%; background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.5rem 0.75rem; border-radius: 6px; font-size: 0.85rem;
  min-height: 140px; box-sizing: border-box; resize: vertical; font-family: monospace;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
`;
const CheckRow = styled.label`
  display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.txtPrincipal};
`;
const HintSmall = styled.small`
  color: ${({ theme }) => theme.colors.txtSecundario}; margin-top: 4px; display: block;
`;
const ErrMsg = styled.small`color: ${({ theme }) => theme.colors.danger}; margin-top: 2px; display: block;`;
const BtnPrimary = styled.button`
  background: ${({ theme }) => theme.colors.info}; color: ${({ theme }) => theme.colors.onPrimary};
  border: none; padding: 0.5rem 1.25rem; border-radius: 6px; font-size: 0.9rem; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.infoHover}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const BtnSecondary = styled.button`
  background: none; border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.5rem 1rem; border-radius: 6px; font-size: 0.9rem; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.surfaceHover}; }
`;
const BtnDanger = styled.button`
  background: ${({ theme }) => theme.colors.danger}; color: ${({ theme }) => theme.colors.onPrimary};
  border: none; padding: 0.5rem 1.25rem; border-radius: 6px; font-size: 0.9rem; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.dangerHover}; }
`;

const BackBtn = styled.button`
  background: none; border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtSecondary}; border-radius: 4px;
  padding: 0.3rem 0.75rem; font-size: 0.8rem; cursor: pointer;
  display: flex; align-items: center; gap: 0.35rem;
  &:hover { border-color: ${({ theme }) => theme.colors.info}; color: ${({ theme }) => theme.colors.info}; }
`;
const TestBtn = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtSecundario};
  border-radius: 4px; padding: 3px 8px; font-size: 0.75rem; cursor: pointer; margin-left: 4px;
  display: inline-flex; align-items: center; gap: 4px;
  &:hover { border-color: ${({ theme }) => theme.colors.info}; color: ${({ theme }) => theme.colors.info}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const TestMsg = styled.div`
  font-size: 0.75rem; color: ${({ $ok, theme }) => $ok ? theme.colors.success : theme.colors.danger};
  margin-top: 4px; padding: 4px 8px;
  background: ${({ $ok, theme }) => $ok ? `${theme.colors.success}11` : `${theme.colors.danger}11`};
  border-radius: 4px; border: 1px solid ${({ $ok, theme }) => $ok ? `${theme.colors.success}33` : `${theme.colors.danger}33`};
`;

// Proveedores con campos estructurados (no se muestra el textarea JSON)
const CRED_FIELDS = {
  powerfleet: [
    { key: 'url',      label: 'URL del portal',  placeholder: 'https://www.monitoreodeflotas.com.ar', type: 'text' },
    { key: 'username', label: 'Usuario',          placeholder: 'Tu nombre de usuario',                 type: 'text' },
    { key: 'password', label: 'Contraseña',       placeholder: '••••••••',                              type: 'password' },
  ],
  geotab: [
    { key: 'server',   label: 'Servidor MyGeotab', placeholder: 'my.geotab.com',    type: 'text' },
    { key: 'database', label: 'Base de datos',     placeholder: 'Nombre del grupo', type: 'text' },
    { key: 'username', label: 'Usuario',            placeholder: 'correo@empresa',  type: 'text' },
    { key: 'password', label: 'Contraseña',         placeholder: '••••••••',         type: 'password' },
  ],
  aemp: [
    { key: 'url',      label: 'URL del endpoint AEMP', placeholder: 'https://...', type: 'text' },
    { key: 'username', label: 'Usuario',               placeholder: '',            type: 'text' },
    { key: 'password', label: 'Contraseña',            placeholder: '••••••••',    type: 'password' },
  ],
  sitrack: [
    { key: 'api_key', label: 'API Key', placeholder: '', type: 'text' },
  ],
  wara: [
    { key: 'api_key', label: 'API Key', placeholder: '', type: 'text' },
  ],
};

const CRED_HINTS = {
  generic_rest: '{"url": "https://...", "headers": {"Authorization": "Bearer ..."}}',
};

const EMPTY = { nombre: '', tipo: 'aemp', credenciales: '{}', activo: true };


export default function ConfiguracionConectoresGPS() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const { onBackdropMouseDown, buildOnBackdropClick } = useBackdropCloseGuard();
  const [jsonError, setJsonError] = useState('');
  // { [id]: 'idle' | 'testing' | 'ok' | 'error', msg }
  const [testState, setTestState] = useState({});

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('conectores-gps/');
      setItems(Array.isArray(r.data) ? r.data : (r.data.results || []));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = useMemo(() => {
    if (!busqueda.trim()) return items;
    const q = busqueda.toLowerCase();
    return items.filter(i => i.nombre?.toLowerCase().includes(q) || i.tipo?.toLowerCase().includes(q));
  }, [items, busqueda]);

  const abrir = (item = null) => {
    setEditando(item);
    if (item) {
      const fields = CRED_FIELDS[item.tipo];
      // Pre-cargar URL y usuario desde credenciales_preview (no sensibles)
      const preview = item.credenciales_preview || {};
      const credInit = fields
        ? {
            url:      preview.url      || '',
            username: preview.username || '',
            server:   preview.server   || '',
            database: preview.database || '',
            api_key:  preview.api_key  || '',
            // contraseña siempre vacía — el usuario la re-ingresa si quiere cambiarla
          }
        : '{}';
      setForm({
        nombre: item.nombre || '',
        tipo: item.tipo || 'aemp',
        activo: item.activo !== false,
        credenciales: credInit,
        _has_password: preview._has_password || false,
      });
    } else {
      setForm({ nombre: '', tipo: 'aemp', activo: true, credenciales: {}, _has_password: false });
    }
    setErrors({});
    setJsonError('');
    setModal(true);
  };

  const cerrar = () => { setModal(false); setEditando(null); };
  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => ({ ...e, [k]: undefined }));
    // Al cambiar el tipo, resetear credenciales al formato correcto
    if (k === 'tipo') {
      const fields = CRED_FIELDS[v];
      setForm(f => ({ ...f, tipo: v, credenciales: fields ? {} : '{}' }));
      setJsonError('');
    }
  };

  const setCred = (key, value) => {
    setForm(f => ({ ...f, credenciales: { ...f.credenciales, [key]: value } }));
  };

  const handleJsonChange = (v) => {
    setForm(f => ({ ...f, credenciales: v }));
    setJsonError('');
    try { JSON.parse(v); } catch { setJsonError('JSON inválido — verificar sintaxis'); }
  };

  const guardar = async () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = 'El nombre es obligatorio';
    if (jsonError) e.credenciales = jsonError;
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      let credObj;
      const fields = CRED_FIELDS[form.tipo];
      if (fields) {
        credObj = {};
        fields.forEach(f => {
          const v = form.credenciales[f.key];
          if (v) credObj[f.key] = v;
          // Si es contraseña y está vacía pero había una guardada, omitir el campo
          // para que el backend conserve la existente (la lógica de merge está en la view)
        });
        if (editando && Object.keys(credObj).length === 0) credObj = undefined;
      } else {
        try { credObj = JSON.parse(form.credenciales); } catch { credObj = {}; }
        if (editando && JSON.stringify(credObj) === '{}') credObj = undefined;
      }
      const payload = { nombre: form.nombre.trim(), tipo: form.tipo, activo: form.activo };
      if (credObj !== undefined) payload.credenciales = credObj;
      if (editando) {
        await apiClient.patch(`conectores-gps/${editando.id}/`, payload);
      } else {
        await apiClient.post('conectores-gps/', payload);
      }
      cerrar(); cargar();
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') setErrors(detail);
    } finally { setSaving(false); }
  };

  const eliminar = async (id) => {
    try { await apiClient.delete(`conectores-gps/${id}/`); setConfirmId(null); cargar(); }
    catch (e) { console.error(e); }
  };

  const probarConexion = async (item) => {
    setTestState(s => ({ ...s, [item.id]: { status: 'testing', msg: '' } }));
    try {
      const r = await apiClient.get(`conectores-gps/${item.id}/previsualizar/`, { timeout: 60000 });
      const vs = Array.isArray(r.data) ? r.data : [];
      setTestState(s => ({
        ...s,
        [item.id]: { status: 'ok', msg: `Conectado — ${vs.length} vehículo${vs.length !== 1 ? 's' : ''} encontrado${vs.length !== 1 ? 's' : ''}` },
      }));
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'No se pudo conectar con el proveedor.';
      setTestState(s => ({ ...s, [item.id]: { status: 'error', msg } }));
    }
  };

  const tipoLabel = (tipo) => TIPOS_GPS.find(t => t.value === tipo)?.label || tipo;
  const formatSync = (dt) => dt ? new Date(dt).toLocaleString('es-AR', { timeZone: ZONA_ARGENTINA }) : 'Nunca';

  return (
    <Page>
      <Header>
        <Title><i className="fas fa-satellite-dish" /> Conectores GPS</Title>
        <HelpToggleBtn
          type="button"
          $active={helpOpen}
          onClick={() => setHelpOpen(v => !v)}
        >
          <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
          Ayuda
        </HelpToggleBtn>
      </Header>

      {helpOpen && (
        <div style={{ marginBottom: '1rem' }}>
          <HelpPanel
            tips={[
              'Cada conector es la conexión con un proveedor de GPS del que se traen las posiciones y las horas de uso.',
              'Necesitás las credenciales que te entrega el proveedor para que la conexión funcione.',
              'Si un conector deja de traer datos, los equipos que dependen de él quedan sin lecturas automáticas.',
            ]}
            nextSteps={[
              'Vincular cada equipo con su dispositivo GPS desde la pantalla de mapeo.',
              'Verificar que lleguen las lecturas antes de dejar de cargar los partes a mano.',
            ]}
            inlineOpen
          />
        </div>
      )}
      <Controls>
        <SearchInput placeholder="Buscar conector..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        <div style={{ display: 'flex', gap: '0.6rem' }}>
          <BackBtn onClick={() => navigate('/configuracion?seccion=gps')}>
            <i className="fas fa-arrow-left" /> Volver
          </BackBtn>
          <AddBtn onClick={() => abrir()}><i className="fas fa-plus" /> Agregar</AddBtn>
        </div>
      </Controls>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Nombre</Th>
              <Th>Proveedor</Th>
              <Th>Estado</Th>
              <Th>Último sync</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><Td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>Cargando...</Td></tr>}
            {!loading && filtrados.length === 0 && <tr><Td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>Sin registros</Td></tr>}
            {filtrados.map(item => {
              const ts = testState[item.id];
              return (
                <tr key={item.id}>
                  <Td>
                    {item.nombre}
                    {ts?.status === 'ok' && (
                      <TestMsg $ok style={{ marginTop: 4 }}>
                        <i className="fas fa-check-circle" style={{ marginRight: 4 }} />{ts.msg}
                      </TestMsg>
                    )}
                    {ts?.status === 'error' && (
                      <TestMsg $ok={false} style={{ marginTop: 4 }}>
                        <i className="fas fa-exclamation-circle" style={{ marginRight: 4 }} />{ts.msg}
                      </TestMsg>
                    )}
                  </Td>
                  <Td>{tipoLabel(item.tipo)}</Td>
                  <Td>
                    <Badge $on={item.activo}>{item.activo ? 'Activo' : 'Inactivo'}</Badge>
                  </Td>
                  <Td>{formatSync(item.fecha_ultimo_sync)}</Td>
                  <ActionsCell>
                    <TestBtn
                      disabled={ts?.status === 'testing'}
                      onClick={() => probarConexion(item)}
                      title="Probar conexión con el proveedor GPS"
                    >
                      {ts?.status === 'testing'
                        ? <><i className="fas fa-circle-notch fa-spin" /> Probando…</>
                        : <><i className="fas fa-link" /> Probar</>
                      }
                    </TestBtn>
                    <ActionBtn onClick={() => abrir(item)}>Editar</ActionBtn>
                    <ActionBtn $danger onClick={() => setConfirmId(item.id)}>Eliminar</ActionBtn>
                  </ActionsCell>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>

      {modal && (
        <Overlay onClick={buildOnBackdropClick(cerrar)} onMouseDown={onBackdropMouseDown}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>{editando ? 'Editar conector GPS' : 'Nuevo conector GPS'}</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={cerrar}>×</CloseBtn>
            </ModalHead>
            <ModalBody>
              <FormGrp>
                <Lbl>Nombre *</Lbl>
                <Inp autoFocus value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Ej: CAT AEMP – Empresa A" />
                {errors.nombre && <ErrMsg>{errors.nombre}</ErrMsg>}
              </FormGrp>
              <FormGrp>
                <Lbl>Proveedor GPS *</Lbl>
                <StyledSelect value={form.tipo} onChange={e => set('tipo', e.target.value)}>
                  {TIPOS_GPS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </StyledSelect>
              </FormGrp>
              {/* Credenciales: campos individuales para proveedores conocidos, JSON libre para generic_rest */}
              {CRED_FIELDS[form.tipo] ? (
                <>
                  {CRED_FIELDS[form.tipo].map(f => {
                    const isPass = f.type === 'password';
                    const hasStored = isPass && editando && form._has_password;
                    return (
                      <FormGrp key={f.key}>
                        <Lbl>{f.label}{!editando ? ' *' : ''}</Lbl>
                        <Inp
                          type={f.type}
                          value={form.credenciales[f.key] || ''}
                          onChange={e => setCred(f.key, e.target.value)}
                          placeholder={
                            hasStored
                              ? '••••••••  (guardada — dejá vacío para no cambiarla)'
                              : f.placeholder
                          }
                          autoComplete={isPass ? 'new-password' : 'off'}
                        />
                        {hasStored && !form.credenciales[f.key] && (
                          <HintSmall style={{ color: 'inherit', opacity: 0.6 }}>
                            <i className="fas fa-lock" style={{ marginRight: 4 }} />
                            Contraseña guardada — solo escribí si querés cambiarla.
                          </HintSmall>
                        )}
                      </FormGrp>
                    );
                  })}
                </>
              ) : (
                <FormGrp>
                  <Lbl>Credenciales (JSON)</Lbl>
                  <Textarea
                    value={form.credenciales}
                    onChange={e => handleJsonChange(e.target.value)}
                    placeholder={CRED_HINTS[form.tipo] || '{}'}
                    spellCheck={false}
                  />
                  {jsonError && <ErrMsg>{jsonError}</ErrMsg>}
                  {CRED_HINTS[form.tipo] && (
                    <HintSmall>Ejemplo: <code>{CRED_HINTS[form.tipo]}</code></HintSmall>
                  )}
                  {editando && <HintSmall>Al dejar <code>{'{}'}</code> no se modifican las credenciales existentes.</HintSmall>}
                </FormGrp>
              )}
              <FormGrp>
                <CheckRow>
                  <input type="checkbox" checked={form.activo} onChange={e => set('activo', e.target.checked)} />
                  Conector activo (se sincroniza en el proceso automático)
                </CheckRow>
              </FormGrp>
            </ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={cerrar}>Cancelar</BtnSecondary>
              <BtnPrimary onClick={guardar} disabled={saving || !!jsonError}>{saving ? 'Guardando...' : 'Guardar'}</BtnPrimary>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}

      {confirmId && (
        <Overlay onClick={buildOnBackdropClick(() => setConfirmId(null))} onMouseDown={onBackdropMouseDown}>
          <ModalBox onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <ModalHead>
              <ModalTitle>Confirmar eliminación</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={() => setConfirmId(null)}>×</CloseBtn>
            </ModalHead>
            <ModalBody><p>¿Eliminar este conector GPS? También se eliminarán sus mapeos. Esta acción no se puede deshacer.</p></ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={() => setConfirmId(null)}>Cancelar</BtnSecondary>
              <BtnDanger onClick={() => eliminar(confirmId)}>Eliminar</BtnDanger>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}
    </Page>
  );
}
